/*
 * List view Mixin (Vue 3)
 *
 * Port da naviga-web/src/mixins/list.js. Tenuto come mixin Vue 3 (fortemente
 * this-bound: listKey/getRows/updateFilterModels forniti dal componente).
 *
 * Ogni componente che lo include DEVE fornire:
 *   - listKey nei data
 *   - i metodi getRows() e updateFilterModels(filters) (se la lista usa filtri)
 *
 * Transform Vue 3:
 *   - vuex mapActions/mapGetters → Pinia: azioni/getter lista su useListStore,
 *     setHasList/isLoading su useRootStore.
 *   - $bus.$on/$off → $bus.on/off con handler memorizzato (this.__onRefresh).
 *   - destroyed → unmounted.
 */

import { mapState, mapActions } from 'pinia';
import { useListStore } from '@/stores/list';
import { useRootStore } from '@/stores/root';

export default {
  methods: {
    ...mapActions(useListStore, [
      'setListRows',
      'setListFilter',
      'setListFilters',
      'setListSorting',
      'setListQuery',
      'setListIsReady',
      'resetList',
    ]),
    ...mapActions(useRootStore, [
      'setHasList',
    ]),
    // List operations
    updateQuery(event) {
      this.setListQuery({ listKey: this.listKey, query: event.isTrusted ? event.target.value.trim() : '' });
      this.refresh();
    },
    updateFilter(field, value, refresh = true) {
      if (!value || !value.length) {
        // eslint-disable-next-line no-unused-vars
        const { [`filter:${field}`]: f, ...filters } = this.listFilters;
        this.setListFilters({ listKey: this.listKey, filters });
      } else {
        this.setListFilter({ listKey: this.listKey, filter: { [`filter:${field}`]: Array.isArray(value) ? value.join(',') : value } });
      }
      if (refresh) this.refresh();
    },
    updateSorting(event) {
      const sorting = { prop: event.prop, order: event.order };
      this.setListSorting({ listKey: this.listKey, sorting });
      this.refresh();
    },
    getSortingFromUrl() {
      const sortings = (this.$route.query.order || '').split(',');
      if (sortings.length > 1) {
        const [prop, order] = (sortings[0] || '').split(':');
        if (prop && order) return { prop, order: order === 'ASC' ? 'ascending' : 'descending' };
      }
      return null;
    },
    fetchFiltersFromUrl() {
      const filters = Object.keys(this.$route.query).reduce((acc, key) =>
        (key.includes('filter') ? { ...acc, [key]: this.$route.query[key] } : acc), {});
      return filters;
    },
    setupList() {
      const sorting = this.getSortingFromUrl();
      if (sorting) this.setListSorting({ listKey: this.listKey, sorting });
      const filters = { ...this.listFilters, ...this.fetchFiltersFromUrl() };
      if (filters && Object.keys(filters).length) {
        this.updateFilterModels(filters);
        this.setListFilters({ listKey: this.listKey, filters });
      }
    },
    // Row operations
    async refresh(force) {
      this.setListIsReady({ listKey: this.listKey, ready: false });
      const rows = await this.getRows(force);
      this.setListRows({ listKey: this.listKey, rows });
      this.setListIsReady({ listKey: this.listKey, ready: true });
    },
    async handleList(force) {
      try {
        return this.refresh(force);
      } catch (err) {
        throw err;
      }
    },
  },
  async mounted() {
    this.setHasList(true);
    this.__onRefresh = () => {
      this.refresh(true);
    };
    this.$bus.on('refresh', this.__onRefresh);
    this.setupList();
    if (this.beforeHandleList) await this.beforeHandleList();
    // refreshOnMount (opt-in del componente): forza il refetch entrando nella pagina,
    // bypassando la cache. Default invariato (cache) per le altre liste.
    await this.handleList(this.refreshOnMount === true);
    if (this.afterHandleList) await this.afterHandleList();
  },
  unmounted() {
    this.setHasList(false);
    this.$bus.off('refresh', this.__onRefresh);
    // this.resetList(this.listKey);
  },
  computed: {
    ...mapState(useRootStore, [
      'isLoading',
    ]),
    ...mapState(useListStore, [
      'listRowsByKey',
      'listQueryByKey',
      'listSortingByKey',
      'listFiltersByKey',
      'listByKeyIsReady',
    ]),
    listRows() {
      return this.listRowsByKey(this.listKey);
    },
    listFilters() {
      return this.listFiltersByKey(this.listKey);
    },
    listIsReady() {
      return this.listByKeyIsReady(this.listKey);
    },
  },
};
